// 도형 추리 — 도형의 변화 규칙을 찾아 다음(또는 빈칸) 도형을 고른다.
// 도형 = 속성 5가지: 변의 수(3~7), 방향(45°씩 8방향, 바늘로 표시), 칠하기(빈·반·꽉), 점 위치(네 귀퉁이), 막대 수(1~5).
// 레벨이 오르면 동시에 바뀌는 속성이 늘고, '번갈아' 규칙과 3×3 행렬이 나온다. 오답은 정답에서 속성 하나만 바꾼 것 → 규칙을 다 맞혀야 고를 수 있다.
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export interface Fig { sides: number; rot: number; fill: number; dot: number; n: number }
export type Attr = keyof Fig;
export interface FigureQuestion extends ChoiceQuestion {
  mode: 'seq' | 'matrix';
  /** seq: 앞의 4개, matrix: 9칸 (마지막이 빈칸 null) */
  cells: (Fig | null)[];
  options: Fig[];
  rule: string;
}

export const FIGURE_COUNT = 6;
const ATTRS: Attr[] = ['sides', 'rot', 'fill', 'dot', 'n'];
const MOD: Record<Attr, number> = { sides: 0, rot: 8, fill: 3, dot: 4, n: 0 }; // 0 = 돌아가지 않음(범위 안에서만 변함)
const RANGE: Record<Attr, [number, number]> = { sides: [3, 7], rot: [0, 7], fill: [0, 2], dot: [0, 3], n: [1, 5] };
const NAME: Record<Attr, string> = { sides: '변의 수', rot: '바늘 방향', fill: '칠하기', dot: '점 위치', n: '막대 수' };
export const figKey = (f: Fig) => `${f.sides}${f.rot}${f.fill}${f.dot}${f.n}`;

interface Rule { kind: 'const' | 'add' | 'alt'; step: number }
const wrap = (a: Attr, v: number) => (MOD[a] ? ((v % MOD[a]) + MOD[a]) % MOD[a] : v);
const valueAt = (a: Attr, base: number, rule: Rule, t: number) =>
  wrap(a, rule.kind === 'add' ? base + rule.step * t : rule.kind === 'alt' ? base + (t % 2) * rule.step : base);

function describe(a: Attr, rule: Rule): string {
  if (rule.kind === 'const') return '';
  if (rule.kind === 'alt') return `${NAME[a]}: 두 가지가 번갈아`;
  if (a === 'rot') return `바늘: ${Math.abs(rule.step) * 45}°씩 ${rule.step > 0 ? '시계' : '반시계'} 방향`;
  if (a === 'dot') return `점: ${rule.step > 0 ? '시계' : '반시계'} 방향으로 ${Math.abs(rule.step)}칸씩`;
  if (a === 'fill') return '칠하기: 빈 → 반 → 꽉 순서로';
  return `${NAME[a]}: ${rule.step > 0 ? '하나씩 늘어남' : '하나씩 줄어듦'}`;
}

const randomFig = (r: Rng): Fig => ({ sides: int(r, 3, 6), rot: int(r, 0, 7), fill: int(r, 0, 2), dot: int(r, 0, 3), n: int(r, 1, 4) });

/** 정답에서 속성 하나만 바꾼 오답들 — 바뀐 속성이 겹치지 않게, 그럴듯한 값(앞 단계 값·한 칸 더 간 값)부터 */
function distractors(r: Rng, answer: Fig, count: number, prefer: Attr[], near: (a: Attr) => number[]): Fig[] {
  const out: Fig[] = [];
  const seen = new Set([figKey(answer)]);
  const order = [...shuffle(r, prefer), ...shuffle(r, ATTRS.filter((a) => !prefer.includes(a)))];
  for (let pass = 0; pass < 3 && out.length < count; pass++) {
    for (const a of order) {
      if (out.length >= count) break;
      const [lo, hi] = RANGE[a];
      const cands = [...near(a), ...shuffle(r, Array.from({ length: hi - lo + 1 }, (_, i) => lo + i))];
      for (const v of cands) {
        const f = { ...answer, [a]: wrap(a, v) };
        if (f[a] < lo || f[a] > hi || seen.has(figKey(f))) continue;
        seen.add(figKey(f));
        out.push(f);
        break;
      }
    }
  }
  return out;
}

function seqQuestion(r: Rng, lv: number): Omit<FigureQuestion, 'answer' | 'n' | 'limitSec' | 'options'> & { answerFig: Fig; wrong: Fig[] } {
  const count = lv <= 2 ? 1 : lv <= 4 ? 2 : lv <= 7 ? 3 : 4;
  const varying = shuffle(r, ATTRS).slice(0, count);
  const base = randomFig(r);
  const rules = {} as Record<Attr, Rule>;
  for (const a of ATTRS) {
    if (!varying.includes(a)) {
      rules[a] = { kind: 'const', step: 0 };
      continue;
    }
    const alt = lv >= 6 && r() < 0.35;
    if (a === 'sides' || a === 'n') {
      // 범위 안에서만: 5단계(보이는 4 + 정답)를 하나씩 늘리거나 줄인다
      const [lo, hi] = RANGE[a];
      if (alt) {
        rules[a] = { kind: 'alt', step: pick(r, [1, 2]) };
        base[a] = int(r, lo, hi - rules[a].step);
      } else {
        const up = r() < 0.5;
        rules[a] = { kind: 'add', step: up ? 1 : -1 };
        base[a] = up ? lo : lo + 4;
      }
      continue;
    }
    if (alt) rules[a] = { kind: 'alt', step: a === 'rot' ? pick(r, [2, 4]) : a === 'dot' ? 2 : 1 };
    else rules[a] = { kind: 'add', step: a === 'rot' ? pick(r, [1, 2, 3, -1, -2]) : a === 'dot' ? pick(r, [1, -1]) : 1 };
  }
  const at = (t: number): Fig => Object.fromEntries(ATTRS.map((a) => [a, valueAt(a, base[a], rules[a], t)])) as unknown as Fig;
  const answerFig = at(4);
  const prev = at(3);
  const two = at(5);
  const wrong = distractors(r, answerFig, lv <= 4 ? 3 : 4, varying, (a) => [prev[a], two[a]]);
  return {
    mode: 'seq', cells: [at(0), at(1), at(2), at(3)], answerFig, wrong,
    rule: varying.map((a) => describe(a, rules[a])).filter(Boolean).join(' · '),
  };
}

function matrixQuestion(r: Rng): Omit<FigureQuestion, 'answer' | 'n' | 'limitSec' | 'options'> & { answerFig: Fig; wrong: Fig[] } {
  const sides = shuffle(r, [3, 4, 5, 6]).slice(0, 3);
  const ns = shuffle(r, [1, 2, 3, 4]).slice(0, 3);
  const fills = shuffle(r, [0, 1, 2]);
  const rot = int(r, 0, 7), dot = int(r, 0, 3);
  const shift = pick(r, [1, 2]); // 행이 바뀔 때 도형 순서가 밀리는 칸 수
  const cell = (i: number, j: number): Fig => ({ sides: sides[(j + i * shift) % 3], n: ns[j], fill: fills[i], rot, dot });
  const cells: (Fig | null)[] = [];
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) cells.push(i === 2 && j === 2 ? null : cell(i, j));
  const answerFig = cell(2, 2);
  const wrong = distractors(r, answerFig, 4, ['sides', 'n', 'fill'], (a) => (a === 'sides' ? sides : a === 'n' ? ns : a === 'fill' ? fills : []));
  return { mode: 'matrix', cells, answerFig, wrong, rule: '가로줄마다 세 도형이 한 번씩, 세로줄마다 막대 수가 같고, 가로줄마다 칠하기가 같다' };
}

export function makeFigureRound(level: number, seed: number): FigureQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const limitSec = lv <= 3 ? 40 : lv <= 6 ? 55 : 70;
  const out: FigureQuestion[] = [];
  for (let k = 0; k < FIGURE_COUNT; k++) {
    const p = lv >= 8 && k % 2 === 1 ? matrixQuestion(rng) : seqQuestion(rng, lv);
    const options = shuffle(rng, [p.answerFig, ...p.wrong]);
    const answer = options.findIndex((f) => figKey(f) === figKey(p.answerFig));
    out.push({ mode: p.mode, cells: p.cells, rule: p.rule, options, answer, n: options.length, limitSec, explain: `규칙 — ${p.rule}` });
  }
  return out;
}
